import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Github, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../layouts/Layout';
import CTA from '../components/CTA';

const PortfolioPage = () => {
    const [portfolios, setPortfolios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 6,
        offset: 0,
        has_more: false
    });

    const filters = [
        { id: 'all', label: 'All Projects' },
        { id: 'web-development', label: 'Web Development' },
        { id: 'mobile-app', label: 'Mobile Apps' },
        { id: 'e-commerce', label: 'E-commerce' },
        { id: 'branding', label: 'Branding' },
        { id: 'other', label: 'Other' }
    ];

    useEffect(() => {
        fetchPortfolios();
        // eslint-disable-next-line
    }, [activeFilter, currentPage]);

    const fetchPortfolios = async () => {
        try {
            setLoading(true);
            setError(null);

            const limit = 6;
            const offset = (currentPage - 1) * limit;

            let url = `/api/portfolio.php?limit=${limit}&offset=${offset}`;

            if (activeFilter !== 'all') {
                url += `&category=${activeFilter}`;
            }

            const response = await fetch(url);
            const result = await response.json();

            if (!result.success) {
                throw new Error(result.error || 'Failed to fetch portfolios');
            }

            setPortfolios(result.data || []);
            setPagination(result.pagination || {});

        } catch (err) {
            console.error('Error fetching portfolios:', err);
            setError(err.message || 'Failed to load portfolios');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterId) => {
        setActiveFilter(filterId);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatCategory = (category) => {
        if (!category) return 'Project';
        return category.split('-').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'long'
            });
        } catch {
            return dateString;
        }
    };

    const totalPages = Math.ceil(pagination.total / pagination.limit);

    return (
        <Layout
            title="Portfolio & Case Studies"
            description="View our portfolio of successful web and mobile development projects. Case studies from FinTech, Healthcare, and E-commerce sectors with real business results."
            keywords="web development portfolio UK, case studies, React projects, mobile app portfolio, enterprise projects, FinTech development, healthcare software"
            path="/portfolio"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto text-center">
                        <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                            Our Project
                            <span className="block text-slate-700 mt-2">Portfolio</span>
                        </h1>

                        <p className="text-xl text-slate-600 mb-12 leading-relaxed max-w-4xl mx-auto">
                            Explore our successful projects across diverse industries. Each case study demonstrates our commitment
                            to delivering measurable business results through innovative technology solutions.
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-slate-200">
                            <div>
                                <div className="text-3xl font-bold text-slate-900">{pagination.total + 163 || 0}</div>
                                <div className="text-slate-600">Projects Completed</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">15+</div>
                                <div className="text-slate-600">Industries Served</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">£50M+</div>
                                <div className="text-slate-600">Value Generated</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-slate-900">98%</div>
                                <div className="text-slate-600">Success Rate</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Filter Section */}
                <section className="py-12 bg-white border-b border-slate-200">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="flex flex-wrap justify-center gap-4">
                            {filters.map((filter) => (
                                <button
                                    key={filter.id}
                                    onClick={() => handleFilterChange(filter.id)}
                                    className={`px-6 py-3 rounded-full font-medium transition-all ${activeFilter === filter.id
                                            ? 'bg-slate-900 text-white'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                        }`}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Projects Grid */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        {loading ? (
                            <div className="space-y-12">
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="grid lg:grid-cols-2 gap-12 items-start animate-pulse">
                                        <div className="h-64 bg-slate-200 rounded-2xl"></div>
                                        <div className="space-y-4">
                                            <div className="h-6 bg-slate-200 rounded w-1/4"></div>
                                            <div className="h-8 bg-slate-200 rounded w-3/4"></div>
                                            <div className="h-4 bg-slate-200 rounded"></div>
                                            <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                                            <div className="h-32 bg-slate-200 rounded"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : error ? (
                            <div className="text-center py-12">
                                <div className="text-4xl text-slate-400 mb-4">⚠️</div>
                                <h3 className="text-xl font-semibold text-slate-900 mb-2">Error Loading Portfolio</h3>
                                <p className="text-slate-600 mb-6">{error}</p>
                                <button
                                    onClick={fetchPortfolios}
                                    className="bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : portfolios.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="text-6xl text-slate-400 mb-4">📁</div>
                                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Projects Found</h3>
                                <p className="text-slate-600">
                                    {activeFilter === 'all'
                                        ? 'No portfolio items available at the moment.'
                                        : `No projects found in the ${formatCategory(activeFilter)} category.`
                                    }
                                </p>
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {portfolios.map((project) => (
                                    <Link
                                        key={project.id}
                                        to={`/portfolio/${project.slug}`}
                                        className="group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                                    >
                                        <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                                            {project.featured_image ? (
                                                <img
                                                    src={project.featured_image}
                                                    alt={project.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                    onError={(e) => {
                                                        e.target.src = '/images/placeholder-project.jpg';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <div className="text-slate-400 text-4xl">🖼️</div>
                                                </div>
                                            )}
                                            {project.featured && (
                                                <div className="absolute top-4 left-4">
                                                    <span className="px-2 py-1 bg-emerald-500 text-white text-xs rounded-full font-medium">
                                                        Featured
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                {project.category && (
                                                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
                                                        {formatCategory(project.category)}
                                                    </span>
                                                )}
                                                {project.client_name && (
                                                    <span className="text-slate-500 text-xs">
                                                        • {project.client_name}
                                                    </span>
                                                )}
                                            </div>

                                            <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors">
                                                {project.title}
                                            </h3>

                                            {project.short_description && (
                                                <p className="text-slate-600 mb-4 line-clamp-3">
                                                    {project.short_description}
                                                </p>
                                            )}

                                            {/* Project Meta */}
                                            <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                                                {project.project_date && (
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {formatDate(project.project_date)}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Technologies */}
                                            <div className="mb-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {Array.isArray(project.technologies) && project.technologies.slice(0, 3).map((tech, techIndex) => (
                                                        <span
                                                            key={techIndex}
                                                            className="px-2 py-1 bg-slate-50 text-slate-600 text-xs rounded font-medium"
                                                        >
                                                            {tech}
                                                        </span>
                                                    ))}
                                                    {Array.isArray(project.technologies) && project.technologies.length > 3 && (
                                                        <span className="px-2 py-1 bg-slate-50 text-slate-600 text-xs rounded font-medium">
                                                            +{project.technologies.length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action Links */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex gap-2">
                                                    {project.project_url && (
                                                        <a
                                                            href={project.project_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-slate-500 hover:text-slate-700 transition-colors"
                                                            title="View Live Project"
                                                        >
                                                            <ExternalLink className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {project.github_url && (
                                                        <a
                                                            href={project.github_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-slate-500 hover:text-slate-700 transition-colors"
                                                            title="View Code"
                                                        >
                                                            <Github className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                </div>
                                                <div className="flex items-center text-slate-600 group-hover:text-slate-900 transition-colors">
                                                    <span className="text-sm font-medium">View Project</span>
                                                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {!loading && !error && totalPages > 1 && (
                            <div className="flex justify-center items-center mt-16 space-x-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </button>

                                <div className="flex space-x-1">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        const isCurrentPage = pageNumber === currentPage;

                                        // Show first page, last page, current page, and pages around current
                                        const showPage = pageNumber === 1 ||
                                            pageNumber === totalPages ||
                                            Math.abs(pageNumber - currentPage) <= 1;

                                        if (!showPage) {
                                            // Show ellipsis for gaps
                                            if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                                                return <span key={pageNumber} className="px-2 text-slate-500">...</span>;
                                            }
                                            return null;
                                        }

                                        return (
                                            <button
                                                key={pageNumber}
                                                onClick={() => handlePageChange(pageNumber)}
                                                className={`w-10 h-10 rounded-lg font-medium transition-colors ${isCurrentPage
                                                        ? 'bg-slate-900 text-white'
                                                        : 'text-slate-600 hover:bg-slate-100'
                                                    }`}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Pagination Info */}
                        {!loading && !error && portfolios.length > 0 && (
                            <div className="text-center mt-8 text-slate-600">
                                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} projects
                            </div>
                        )}
                    </div>
                </section>

                {/* Technologies Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Technologies We Master</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Our expertise spans the entire technology stack, enabling us to choose the best tools for each project
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                {
                                    category: "Frontend",
                                    technologies: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Vue.js"]
                                },
                                {
                                    category: "Backend",
                                    technologies: ["Node.js", "PHP", "Python", "Express", "Laravel"]
                                },
                                {
                                    category: "Mobile",
                                    technologies: ["React Native", "iOS", "Android", "Flutter", "PWA"]
                                },
                                {
                                    category: "Infrastructure",
                                    technologies: ["AWS", "Docker", "PostgreSQL", "MongoDB", "Redis"]
                                }
                            ].map((category, index) => (
                                <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-900 mb-4">{category.category}</h3>
                                    <div className="space-y-2">
                                        {category.technologies.map((tech, techIndex) => (
                                            <div key={techIndex} className="text-slate-600">{tech}</div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default PortfolioPage;